const EMPTY_PRESET = {
  objective: "",
  structure: "",
  audience: "",
  context: "",
  focus: "",
  slideCount: "",
  additional: "",
};

export const PRESET_VALUES = {
  quantitative: {
    default: {
      ...EMPTY_PRESET,
      objective:
        "Provide a complete analysis of survey results with key insights and actionable recommendations",
      structure:
        "Cover slide, executive summary, then thematic sections. Each section with charts and a short analysis highlighting key findings. End with key takeaways and recommendations.",
      audience: "Executive audience, professional and analytical tone",
    },
    basic: {
      ...EMPTY_PRESET,
      objective: "Present raw survey results without interpretation",
      structure:
        "Cover slide, then one slide per survey question grouped by theme with section dividers. Use question titles as slide titles, one chart per slide showing raw distribution, no commentary.",
      audience: "Neutral and factual, no specific audience",
    },
    empty: EMPTY_PRESET,
  },
  qualitative: {
    default: {
      ...EMPTY_PRESET,
      objective:
        "Provide a qualitative analysis of the interviews: key themes, drivers and barriers, with insights grounded in respondents' own words",
      structure:
        "Cover slide, table of contents, methodology, then a respondent profile slide. Then thematic sections anchored on the study objectives: each section combines insight slides grounded in respondent verbatims, with charts only where a number strengthens the story. Then a personas section. Close with an executive summary, then key takeaways and recommendations.",
      audience: "Executive audience, professional and analytical tone",
    },
    basic: {
      ...EMPTY_PRESET,
      objective: "Present what respondents said, theme by theme, without interpretation",
      structure:
        "Cover slide, then one slide per interview objective, using the verbatim templates to showcase representative quotes. No analysis commentary.",
      audience: "Neutral and factual, no specific audience",
    },
    empty: EMPTY_PRESET,
  },
};

export const PRESET_OPTIONS = {
  quantitative: [
    { value: "empty", label: "No preset", description: "Fully customized instructions" },
    { value: "basic", label: "Basic report preset", description: "Raw results, one slide per question" },
    { value: "default", label: "Advanced report preset", description: "Full AI analysis with insights" },
  ],
  qualitative: [
    { value: "empty", label: "No preset", description: "Fully customized instructions" },
    { value: "basic", label: "Voices only", description: "Representative quotes, theme by theme" },
    { value: "default", label: "Insights & verbatims", description: "Thematic analysis grounded in quotes" },
  ],
};

export const WIZARD_FIELDS = [
  {
    key: "objective",
    label: "What kind of report do you need?",
    subtitle: "Describe the report's purpose and objectives.",
    promptLabel: "Report objective",
    type: "textarea",
  },
  {
    key: "slideCount",
    label: "Approximate number of slides?",
    subtitle: "Leave empty to let the AI decide.",
    promptLabel: "Number of slides",
    type: "number",
  },
  {
    key: "structure",
    label: "How should the report be structured?",
    subtitle:
      "Describe the overall flow and slide organization. If you have a specific outline in mind, describe it here.",
    promptLabel: "Structure",
    type: "textarea",
  },
  {
    key: "audience",
    label: "Who is the audience and what tone should be used?",
    subtitle:
      "e.g. executive committee with a formal tone, marketing team with an action-oriented approach...",
    promptLabel: "Audience and tone",
    type: "textarea",
  },
  {
    key: "context",
    label: "What is the business context and objectives of this survey?",
    subtitle: "Any background that helps the AI better understand and interpret the data.",
    promptLabel: "Business context",
    type: "textarea",
  },
  {
    key: "focus",
    label: "Any specific content or focus areas?",
    subtitle:
      "You can specify themes to emphasize, or paste an existing analysis that the AI should incorporate into the report.",
    promptLabel: "Focus areas",
    type: "textarea",
  },
  {
    key: "additional",
    label: "Anything else?",
    subtitle: "Any other preferences or constraints for the report.",
    promptLabel: "Additional instructions",
    type: "textarea",
  },
];

export function buildPromptFromValues(formValues) {
  const parts = ["Please create a report based on the following instructions:"];
  for (const field of WIZARD_FIELDS) {
    const raw = formValues[field.key];
    const value = typeof raw === "string" ? raw.trim() : raw;
    if (value) {
      parts.push(`${field.promptLabel}: ${value}`);
    }
  }
  return parts.join("\n\n");
}
